import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import { TFunction } from 'i18next';
import { LOCALE_COMMON_PATHS } from 'types/locales';
import textTransform from 'utils/text-transform';

/**
 * Represents a key-value item used for displaying property details in tables and lists.
 *
 * This type is commonly used throughout the address description component to represent
 * individual property features, amenities, and details that get displayed as label-value pairs.
 * The label is typically a human-readable description (often a translation key), while the
 * value contains the actual data for that property feature.
 *
 * @example
 * ```typescript
 * const heatField: KeyValueItem = {
 *   label: 'common:address.description_heat',
 *   value: 'Gas'
 * };
 *
 * const acField: KeyValueItem = {
 *   label: 'common:address.description_ac',
 *   value: true  // Will be converted to "Yes" during display
 * };
 *
 * const basementField: KeyValueItem = {
 *   label: 'common:address.description_basement',
 *   value: 'Finished'
 * };
 * ```
 */
export type KeyValueItem = {
  /** The display label for the property field. Usually a translation key or formatted text. */
  label: string;
  /** The value of the property field. Can be a string, number, boolean, or null. Boolean values are typically converted to "Yes"/"No" during display. */
  value?: string | number | boolean | null;
};

/**
 * Represents a collection of property fields used to display comprehensive property information.
 *
 * This type is used for organizing multiple property details into structured sections like:
 * - Property features (heat, A/C, heating fuel, etc.)
 * - Maintenance fees (heat included, hydro included, etc.)
 * - Building amenities (exterior, garage, etc.)
 * - Unit properties (ensuite laundry, balcony, etc.)
 *
 * The array can contain null values which are filtered out during rendering to handle
 * conditional property fields that may not apply to all property types.
 *
 * @example
 * ```typescript
 * const utilityFields: PropertyFields = [
 *   { label: 'Heat', value: 'Gas' },
 *   { label: 'A/C', value: true },
 *   { label: 'Heating Fuel', value: 'Natural Gas' },
 *   null, // Conditional field that gets filtered out
 *   { label: 'Water', value: 'Municipal' }
 * ];
 *
 * // Used in the description component for displaying property details
 * const detailsFields: PropertyFields = buildOptions(listing, t, ...UTILITIES_FIELDS);
 * ```
 *
 * @remarks
 * - Null values in the array are typically filtered out using `.filter(Boolean)` before rendering
 * - The label field is always required and is usually a translation key
 * - Boolean values are converted to localized "Yes"/"No" strings during display
 * - This type is used extensively in the address description component for various property detail sections
 */
export type PropertyFields = (KeyValueItem | null)[];

/**
 * Builds an array of PropertyFields objects from a source object and property names.
 *
 * This function extracts values from a source object and creates
 * formatted PropertyFields objects with labels and values. It handles boolean values and string "Yes"/"No" values
 * by converting them to translated strings (Yes) -> "common:address.description_options_yes" / (No) -> "common:address.description_options_no"
 * and supports custom label overrides using a hash syntax.
 *
 * @param source The source object to extract values from. Can be any object with string keys
 *                    or a Listing object. If null, all properties will have undefined values.
 * @param t The translation function from i18next
 * @param properties Variable number of property names to extract. Can use "propertyName#Custom Label"
 *                     syntax to override the default label formatting.
 *
 * @returns An array of option objects with label and value properties. Each option may be null
 *          if the corresponding property doesn't exist in the source object.
 *
 * @example
 * ```typescript
 * const source = { heat: true, ac: false, garage: 'Yes', heatingFuel: 'Gas' };
 *
 * // Basic usage - handles booleans and string "Yes"/"No" values
 * createPropertyFields(source, t, 'heat', 'ac', 'garage', 'heatingFuel')
 * // Returns: [
 * //   { label: 'Heat', value: 'common:address.description_options_yes' },
 * //   { label: 'Ac', value: 'common:address.description_options_no' },
 * //   { label: 'Garage', value: 'common:address.description_options_yes' },
 * //   { label: 'Heating Fuel', value: 'Gas' }
 * // ]
 *
 * // With custom labels
 * createPropertyFields(source, t, 'heat#Heating System', 'ac#A/C', 'heatingFuel#Fuel Type')
 * // Returns: [
 * //   { label: 'Heating System', value: 'common:address.description_options_yes' },
 * //   { label: 'A/C', value: 'common:address.description_options_no' },
 * //   { label: 'Fuel Type', value: 'Gas' }
 * // ]
 *
 * // With null source
 * createPropertyFields(null, t, 'heat', 'ac')
 * // Returns: [
 * //   { label: 'Heat', value: undefined },
 * //   { label: 'Ac', value: undefined }
 * // ]
 * ```
 */
export function createPropertyFields<T extends Record<string, unknown> | null>(source: T, t: TFunction, ...properties: string[]): PropertyFields {
  return properties.map(property => {
    const [name, label] = property.split('#');
    let key: unknown;
    if (source) {
      key = name in source ? source[name] : undefined;
    }
    let value: string | number | boolean | null;
    if (key === true) {
      value = t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_OPTIONS_YES);
    } else if (key === false) {
      value = t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_OPTIONS_NO);
    } else if (typeof key === 'string') {
      // Check if string is "Yes" or "No" (case-insensitive) and translate them
      const normalizedKey = key.toLowerCase();
      if (normalizedKey === 'yes') {
        value = t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_OPTIONS_YES);
      } else if (normalizedKey === 'no') {
        value = t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_OPTIONS_NO);
      } else {
        value = key;
      }
    } else if (typeof key === 'number' || key === null) {
      value = key as number | null;
    }

    return {
      label: label || capitalizeWords(textTransform(name, 'deCamelize')),
      value,
    };
  });
}

/**
 * Returns `true` when a property field value should be treated as "unavailable" and hidden.
 *
 * `null`, `undefined` and empty/whitespace-only strings are considered empty. Numbers (including `0`)
 * and booleans are considered present, as are non-empty strings (e.g. localized "Yes"/"No" values).
 */
export function isEmptyFieldValue(value?: string | number | boolean | null): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  return false;
}

/** Feature flags using bitmap approach to control what sections are displayed on the address page */
export const FEATURE_FLAGS = {
  /** Whether to use priority mode for displaying similar listings. If true, it will load the similar listings before the price history. */
  USE_SIMILAR_LISTINGS_PRIORITY_MODE: 1 << 0,
  /** Whether to display the price history. Default is true. */
  PRICE_HISTORY_ENABLED: 1 << 1,
  /** Whether to display the provider disclaimer. Default is true. */
  PROVIDER_DISCLAIMER_ENABLED: 1 << 2,
  /** Whether to display the `my link my lead` banner on the tenants that have the feature enabled. Default is true. */
  MY_LINK_MY_LEAD_ENABLED: 1 << 3,
  /** Whether to display the property appraisal. Default is true. */
  PROPERTY_APPRAISAL_ENABLED: 1 << 4,
  /** Whether to display the charts. Default is true. */
  CHARTS_ENABLED: 1 << 5,
  /** Whether to display the secondary info section. Default is true. */
  SECONDARY_INFO_ENABLED: 1 << 6,
  /** Whether to use carousel priority mode. */
  USE_CAROUSEL_PRIORITY_MODE: 1 << 7,
  /** Whether to enable walk score. */
  WALK_SCORE_ENABLED: 1 << 8,
  /** Whether internal links are active. */
  INTERNAL_LINKS_ACTIVE: 1 << 9,
  /** Whether to display the breadcrumbs. */
  BREADCRUMBS_ENABLED: 1 << 10,
  /** Whether to display the similar listings section. */
  SIMILAR_LISTINGS_ENABLED: 1 << 11,
  /** Whether to display the comparable listings section. */
  COMPARABLE_LISTINGS_ENABLED: 1 << 12,
  /** Whether to display the agent cards on the address/listing page. */
  AGENT_CARDS_ENABLED: 1 << 13,
  /** Whether to use Hero V2 design (dev-only for now, controlled by tenant config). */
  HERO_V2_ENABLED: 1 << 14,
} as const;

/** Default feature flags - all features enabled by default */
export const DEFAULT_FEATURE_FLAGS =
  FEATURE_FLAGS.PRICE_HISTORY_ENABLED |
  FEATURE_FLAGS.PROVIDER_DISCLAIMER_ENABLED |
  FEATURE_FLAGS.MY_LINK_MY_LEAD_ENABLED |
  FEATURE_FLAGS.CHARTS_ENABLED |
  FEATURE_FLAGS.SECONDARY_INFO_ENABLED |
  FEATURE_FLAGS.WALK_SCORE_ENABLED |
  FEATURE_FLAGS.BREADCRUMBS_ENABLED |
  FEATURE_FLAGS.SIMILAR_LISTINGS_ENABLED |
  FEATURE_FLAGS.COMPARABLE_LISTINGS_ENABLED |
  FEATURE_FLAGS.HERO_V2_ENABLED; // Enabled but gated by NODE_ENV in component

/**
 * Helper function to check if a specific feature flag is set
 * @param flags The bitmap of feature flags
 * @param flag The specific flag to check
 * @returns true if the flag is set, false otherwise
 */
export const hasFeatureFlag = (flags: number, flag: number): boolean => !!(flags & flag);

/**
 * Helper function to set a specific feature flag
 * @param flags The current bitmap of feature flags
 * @param flag The flag to set
 * @returns new bitmap with the flag set
 */
export const setFeatureFlag = (flags: number, flag: number): number => flags | flag;

/**
 * Helper function to clear a specific feature flag
 * @param flags The current bitmap of feature flags
 * @param flag The flag to clear
 * @returns new bitmap with the flag cleared
 */
export const clearFeatureFlag = (flags: number, flag: number): number => flags & ~flag;

/**
 * Helper function to toggle a specific feature flag
 * @param flags The current bitmap of feature flags
 * @param flag The flag to toggle
 * @returns new bitmap with the flag toggled
 */
export const toggleFeatureFlag = (flags: number, flag: number): number => flags ^ flag;